// eslint-disable-next-line import/no-extraneous-dependencies
import ReactQuill, { Quill } from "react-quill";
import { Popover } from "bootstrap";
import Styles from "./Editor.module.scss";

type QuillInstance = InstanceType<(typeof ReactQuill)["Quill"]>;

// eslint-disable-next-line max-len
const URL_REGEXP = /((https?)|(mailto)|(tel)|(ftp)):\/\/(?:w{1,3}\.)?[^\s.]+(?:\.[a-z]+)*(?::\d+)?(?![^<]*(?:<\/\w+>|\/?>))/;
const ALLOWED_PROTOCOLS = ["http://", "https://", "ftp://", "mailto://", "tel://"];

function createContainer() {
    const container = document.createElement("div");
    container.style.display = "none";
    container.classList.add("modal", Styles["links-modal"]);

    const show = () => {
        container.style.display = "block";
    };
    const hide = () => {
        container.style.display = "none";
    };

    const dialog = document.createElement("div");
    dialog.classList.add("modal-dialog");
    container.appendChild(dialog);

    const content = document.createElement("form");
    content.classList.add("modal-content");
    dialog.appendChild(content);

    const header = document.createElement("div");
    header.classList.add("modal-header");
    content.appendChild(header);

    const title = document.createElement("h5");
    title.classList.add("modal-title");
    title.textContent = "Link";
    header.appendChild(title);

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.classList.add("btn-close", "m-0");
    closeButton.setAttribute("aria-label", "Close");
    closeButton.addEventListener("click", hide);
    closeButton.addEventListener("submit", () => false);
    header.appendChild(closeButton);

    const body = document.createElement("div");
    body.classList.add("modal-body");
    content.appendChild(body);

    const textGroup = document.createElement("div");
    textGroup.classList.add("form-group");
    body.appendChild(textGroup);

    const labelText = document.createElement("label");
    labelText.textContent = "Text";
    labelText.classList.add("form-label");
    textGroup.appendChild(labelText);

    const textBox = document.createElement("input");
    textBox.placeholder = "My Link";
    textBox.type = "text";
    textBox.required = true;
    textBox.classList.add("form-control", "form-control-sm");
    textGroup.appendChild(textBox);

    const linkGroup = document.createElement("div");
    linkGroup.classList.add("form-group");
    body.appendChild(linkGroup);

    const labelLink = document.createElement("label");
    labelLink.textContent = "Link";
    labelLink.classList.add("form-label");
    linkGroup.appendChild(labelLink);

    const linkBox = document.createElement("input");
    linkBox.placeholder = "https://foqal.io";
    linkBox.type = "url";
    linkBox.required = true;
    linkBox.classList.add("form-control", "form-control-sm");
    linkGroup.appendChild(linkBox);

    const footer = document.createElement("div");
    footer.classList.add("modal-footer");
    content.appendChild(footer);

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.classList.add("btn", "btn-secondary");
    cancelButton.textContent = "Close";
    cancelButton.addEventListener("click", hide);
    cancelButton.addEventListener("submit", () => false);
    footer.appendChild(cancelButton);

    const saveButton = document.createElement("button");
    saveButton.type = "submit";
    saveButton.classList.add("btn", "btn-primary");
    saveButton.textContent = "Save";
    footer.appendChild(saveButton);

    let savehandler: () => void;
    content.addEventListener("submit", event => {
        event.preventDefault();
        if (!content.checkValidity()) {
            return;
        }
        savehandler?.();
        hide();
    });

    const onSave = (handler: (() => void)) => {
        savehandler = handler;
    };

    return {
        container, textBox, linkBox, show, hide, saveButton, onSave,
    };
}

interface LinkType {
    text: string;
    url: string;
}

const MODAL = createContainer();
const WAS_MOUNTED = false;

export default function createLinkButton() {
    if (!WAS_MOUNTED) {
        document.body.appendChild(MODAL.container);
    }
    // eslint-disable-next-line func-names
    return function (this: {quill: QuillInstance}) {
        const position = this.quill.getSelection();

        if (!position) {
            return;
        }

        const insertText = (text: string, url: string) => {
            this.quill.deleteText(position.index, position.length, "silent");
            this.quill.insertText(position.index ?? 0, text, "link", url, "silent");

            setTimeout(() => this.quill.setSelection(position.index + position.length, 0, "silent"));
        };

        const selectedText = this.quill.getText(position.index, position.length);
        const isLink = URL_REGEXP.test(selectedText);

        if (isLink) {
            insertText(selectedText, selectedText);
            return;
        }

        MODAL.onSave(() => {
            const text = MODAL.textBox.value;
            const url = MODAL.linkBox.value;
            insertText(text, url);
        });
        MODAL.textBox.value = selectedText;
        if (isLink) {
            MODAL.linkBox.value = ALLOWED_PROTOCOLS.some(proto => selectedText.startsWith(proto))
                ? selectedText
                : `https://${selectedText}`;
        } else {
            MODAL.linkBox.value = "";
        }

        MODAL.show();
        MODAL.linkBox.focus();
    };
}

const Inline = Quill.import("blots/inline");

class LinkBlot extends Inline {
    static create(value: LinkType) {
        const link = super.create();
        link.setAttribute("href", value);
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
        // link.textContent = value.text;
        link.addEventListener("click", () => {
            // eslint-disable-next-line no-param-reassign
            MODAL.textBox.value = link.textContent as string;
            // eslint-disable-next-line no-param-reassign
            MODAL.linkBox.value = link.getAttribute("href") as string;
            MODAL.show();

            MODAL.onSave(() => {
                const text = MODAL.textBox.value;
                const url = MODAL.linkBox.value;
                link.textContent = text;
                link.setAttribute("href", url);
            });
        });
        return link;
    }

    static formats(domNode: HTMLAnchorElement) {
        return domNode.getAttribute("href");
    }

    static value(url: LinkType) {
        return {
            text: url.text,
            url: url.url,
        };
    }
}

LinkBlot.blotName = "link";
LinkBlot.tagName = "a";

Quill.register("formats/link", LinkBlot);
